import { memo, useCallback, type ChangeEvent } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { FormDialog } from "../../../components/primitives/feedback/FormDialog/FormDialog";
import type { BatchJobKind } from "../../../lib/corpusApi";
import { AdmissionRecheckParamsForm } from "./kinds/admission_recheck";
import { FieldReannotationParamsForm } from "./kinds/field_reannotation";
import { PubmedIngestParamsForm } from "./kinds/pubmed_ingest";
import { DocumentSectionsRefreshParamsForm } from "./kinds/document_sections_refresh";
import { DocumentIndexingRefreshParamsForm } from "./kinds/document_indexing_refresh";
import { BATCH_JOB_KIND_OPTIONS } from "./batch_job_kinds";
import { useNewBatchJobDialog } from "./useNewBatchJobDialog";

export interface NewBatchJobDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** Collects a batch job kind and that kind's params, creates the job, and navigates to its detail
 *  page on success. The Create button stays disabled until the chosen kind's params are enough —
 *  for a PubMed query ingest, until the query has been validated. */
export const NewBatchJobDialog = memo(function NewBatchJobDialog({
  open,
  onClose,
  onCreated,
}: NewBatchJobDialogProps) {
  const {
    kind,
    setKind,
    admissionRecheck,
    pubmedIngest,
    fieldReannotation,
    documentIndexingRefresh,
    name,
    setName,
    canCreate,
    dialogProps,
  } = useNewBatchJobDialog({ open, onClose, onCreated });

  const handleKindChange = useCallback(
    (e: SelectChangeEvent) => setKind(e.target.value as BatchJobKind),
    [setKind]
  );

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setName(e.target.value),
    [setName]
  );

  return (
    <FormDialog
      open={open}
      title="New batch job"
      submitLabel="Create job"
      submitDisabled={!canCreate}
      maxWidth="sm"
      {...dialogProps}
    >
      <TextField
        label="Name"
        size="small"
        fullWidth
        value={name}
        onChange={handleNameChange}
        placeholder="Defaults to: your name - kind - date"
        slotProps={{ htmlInput: { maxLength: 120 } }}
      />
      <FormControl fullWidth size="small">
        <InputLabel id="batch-job-kind-label">Kind</InputLabel>
        <Select
          labelId="batch-job-kind-label"
          label="Kind"
          value={kind}
          onChange={handleKindChange}
        >
          {BATCH_JOB_KIND_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {kind === "admission_recheck" && <AdmissionRecheckParamsForm form={admissionRecheck} />}
      {kind === "pubmed_ingest" && <PubmedIngestParamsForm form={pubmedIngest} />}
      {kind === "field_reannotation" && <FieldReannotationParamsForm form={fieldReannotation} />}
      {kind === "document_indexing_refresh" && (
        <DocumentIndexingRefreshParamsForm form={documentIndexingRefresh} />
      )}
      {kind === "document_sections_refresh" && <DocumentSectionsRefreshParamsForm />}
    </FormDialog>
  );
});
